<template>
  <div class="message-box dialog-mask" @click.self="onClickOutSide">
    <div class="dialog-box">
      <div class="section dialog-header">
        <h1>{{ $t('plotPage.plotCreateDialog.title') }}</h1>
      </div>
      <div class="section dialog-body">
        <div class="input-container">
          <h2>{{ $t('plotPage.plotCreateDialog.plotName') }}</h2>
          <input-text v-model="name" :maxlength="120" />
        </div>
        <div class="input-container">
          <h2>{{ $t('plotPage.plotCreateDialog.selectPlotFormat') }}</h2>
          <select-box
            class="selectbox"
            :class="{ disabled: hasLimit }"
            :items="plotModes"
            keyAttr="id"
            :placeholder="$t('plotPage.plotCreateDialog.selectPlaceholder')"
            @select="selectMode"
            :selected="selectedMode"
            :disabled="hasLimit"
          />
          <div v-if="hasLimit" class="caution">{{ $t('plotPage.plotCreateDialog.freeMemberLimit') }}</div>
        </div>
      </div>
      <div class="section button-area">
        <button class="button cancel" @click="onNegativeClick">{{ negative }}</button>
        <button class="button primary" @click="onPositiveClick" :disabled="!isCompleted">
          {{ positive }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import InputText from "@/components/atoms/InputText.vue";
import { CreateSubPlotPayload, Mode, Layout, PlotGroups, DisplayFormat } from "@/lib/models/plot";
import { plotModes, PlotModeSelectBoxItem } from "@/lib/plot";
import SelectBox from "@/components/molecules/SelectBox.vue";

export default Vue.extend<Data, Methods, Computed, Props>({
  components: { InputText, SelectBox },
  props: {
    novelId: {
      type: String as PropType<string>,
    },
    hasLimit: Boolean,
    negative: {
      type: String,
      default() {
        return this.$t('common.cancel');
      },
    },
    positive: {
      type: String,
      default() {
        return this.$t('plotPage.plotCreateDialog.createButton');
      },
    },
    negativeCallback: Function,
    positiveCallback: Function,
  },
  data() {
    return {
      name: "",
      selectedMode: plotModes[0],
      plotModes,
    };
  },
  methods: {
    onNegativeClick() {
      if (this.negativeCallback) {
        this.negativeCallback();
      }

      this.$close(true);
    },
    onPositiveClick() {
      if (this.positiveCallback) {
        this.positiveCallback();
      }

      const { $router, novelId, name, selectedMode } = this;
      const mode = selectedMode.id;
      const layout = Layout.simple;
      const displayFormat = DisplayFormat.HORIZONTAL;

      let plotGroups: PlotGroups[] = [];

      // eslint-disable-next-line default-case
      switch (mode) {
        default:
        case Mode.kishoutenketsu:
          plotGroups = [
            { name: this.$t('plotPage.plotCreateDialog.plotGroups.kishoutenketsu.ki') as string, description: "", plots: [] },
            { name: this.$t('plotPage.plotCreateDialog.plotGroups.kishoutenketsu.shou') as string, description: "", plots: [] },
            { name: this.$t('plotPage.plotCreateDialog.plotGroups.kishoutenketsu.ten') as string, description: "", plots: [] },
            { name: this.$t('plotPage.plotCreateDialog.plotGroups.kishoutenketsu.ketsu') as string, description: "", plots: [] },
          ];
          break;
        case Mode.johakyu:
          plotGroups = [
            { name: this.$t('plotPage.plotCreateDialog.plotGroups.johakyu.jo') as string, description: "", plots: [] },
            { name: this.$t('plotPage.plotCreateDialog.plotGroups.johakyu.ha') as string, description: "", plots: [] },
            { name: this.$t('plotPage.plotCreateDialog.plotGroups.johakyu.kyu') as string, description: "", plots: [] },
          ];
          break;
        case Mode.custom:
          plotGroups = [
            { name: this.$t('plotPage.plotCreateDialog.plotGroups.custom.chapter1') as string, description: "", plots: [] },
            { name: this.$t('plotPage.plotCreateDialog.plotGroups.custom.chapter2') as string, description: "", plots: [] },
            { name: this.$t('plotPage.plotCreateDialog.plotGroups.custom.chapter3') as string, description: "", plots: [] },
            { name: this.$t('plotPage.plotCreateDialog.plotGroups.custom.chapter4') as string, description: "", plots: [] },
          ];
          break;
      }

      const createSubPlotPayload: CreateSubPlotPayload = {
        novelId,
        name,
        plotGroups,
        mode,
        layout,
        displayFormat,
        callback: (plotId: string) => {
          $router.push({ name: "plotEdit", params: { novelId, plotId } });
        },
      };
      this.$store.dispatch("plotModule/createSubPlot", createSubPlotPayload);

      this.$close(true);
    },
    onClickOutSide() {
      this.$close(true);
    },
    selectMode(item) {
      this.selectedMode = item;
    },
  },
  computed: {
    isCompleted() {
      const { name } = this;
      return !!name;
    },
  },
});

interface Props {
  novelId: string;
  hasLimit: boolean;
  negative?: string;
  positive?: string;
  negativeCallback?: () => void;
  positiveCallback?: () => void;
}

interface Data {
  name: string;
  selectedMode: PlotModeSelectBoxItem;
  plotModes: PlotModeSelectBoxItem[];
}

interface Methods {
  onNegativeClick: () => void;
  onPositiveClick: () => void;
  onClickOutSide: () => void;
  selectMode(item: PlotModeSelectBoxItem): void;
}

interface Computed {
  isCompleted: boolean;
}

export type PlotCreateDialogProps = Props;
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.fade-enter-active {
  transition: opacity 0.3s;
}

.fade-leave-active {
  transition: opacity 0.1s;
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

.dialog-mask {
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: -ms-flexbox;
  display: flex;
  z-index: 9000;
  -ms-flex-align: center;
  align-items: center;
  -ms-flex-pack: center;
  justify-content: center;
  background-color: var(--color-overlay);
}

.dialog-box {
  min-width: 350px;
  margin: 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background-color: var(--color-background-normal);
  box-shadow: var(--shadow-md);
  color: var(--color-text-normal);

  .section {
    border-bottom: 1px solid var(--color-border);
    box-sizing: border-box;
    padding: 20px;
  }

  .dialog-header {
    h1 {
      text-align: center;
      margin: 0;
      font-size: 1.5em;
    }
  }

  .dialog-body {
    position: relative;
    font-size: 1.4em;
    font-weight: var(--font-weight-bold);
    background-color: var(--color-surface-subtle);

    .input-container {
      margin-bottom: 1em;

      &:last-of-type {
        margin-bottom: 0;
      }
    }

    h2 {
      margin: 0;
      margin-bottom: 10px;
      font-size: 0.8em;
      font-weight: var(--font-weight-regular);
    }

    input {
      box-sizing: border-box;
      width: 100%;
    }

    .selectbox {
      background-color: var(--color-background-normal);

      &.disabled {
        ::v-deep li {
          cursor: not-allowed;
        }
      }
    }

    .caution {
      color: var(--color-text-low);
      font-size: 0.75em;
      margin-top: 10px;
    }
  }

  .button-area {
    border-bottom: none;
    display: flex;
    justify-content: center;

    .button {
      position: relative;
      width: 100px;
      margin: 0;
      font-size: 1.1em;
      box-sizing: border-box;
      border-radius: var(--radius-xs);
      font-weight: var(--font-weight-bold);
      cursor: pointer;
      @include hover-overlay;

      &:last-of-type {
        margin-left: 20px;
      }

      &.cancel {
        background-color: var(--color-inactive);
        border: solid 1px var(--color-inactive);
        color: var(--color-text-on-fixed-bg);
      }

      &.primary {
        background-color: var(--color-primary);
        border: solid 1px var(--color-primary);

        &:not(:disabled) {
          color: var(--color-text-on-fixed-bg);
        }

        &:disabled {
          background-color: var(--color-surface-muted);
          border: solid 1px var(--color-border);
          color: var(--color-inactive);
          cursor: not-allowed;

          &::after {
            display: none;
          }
        }
      }
    }
  }
}
</style>
